import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileUpload } from '../components/FileUpload';
import { FilterPanel } from '../components/FilterPanel';
import { ProjectTable } from '../components/ProjectTable';
import { Settings } from '../components/Settings';
import { Project, Filter } from '../types';
import { Database, Settings as SettingsIcon, Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { ProjectModal } from '../components/ProjectModal';

export function Projects() {
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<Filter>({
    projectName: '',
    customerName: searchParams.get('customer') || '',
    selectedTags: [],
    selectedStaff: [],
    year: '',
    duration: '',
    budget: '',
    featured: false
  });

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    // Apply filter whenever the customer parameter changes
    const customerParam = searchParams.get('customer');
    if (customerParam) {
      const newFilter = {
        ...currentFilter,
        customerName: customerParam
      };
      setCurrentFilter(newFilter);
      if (projects.length > 0) {
        applyFilter(newFilter);
      }
    }
  }, [searchParams, projects]);

  const loadProjects = async () => {
    try {
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select(`
          id,
          project_name,
          description,
          year,
          month,
          budget,
          workload,
          featured,
          created_at,
          updated_at,
          customer:customers (
            id,
            name
          ),
          duration:durations (
            id,
            name
          ),
          images:project_images (
            id,
            url,
            video_url,
            media_type,
            display_order,
            created_at
          ),
          tags:project_tags (
            tag:tags (
              id,
              name
            )
          ),
          staff:project_staff (
            staff:staff (
              id,
              name
            )
          )
        `)
        .order('updated_at', { ascending: false });

      if (projectsError) {
        throw projectsError;
      }

      const loadedProjects: Project[] = projectsData.map(project => ({
        id: project.id,
        projectName: project.project_name,
        description: project.description,
        year: project.year,
        month: project.month,
        budget: project.budget,
        workload: project.workload,
        featured: project.featured,
        duration: project.duration,
        customer: project.customer || { id: '', name: '' },
        images: project.images || [],
        tags: project.tags?.map((pt: any) => pt.tag) || [],
        staff: project.staff?.map((ps: any) => ps.staff) || [],
        created_at: project.created_at,
        updated_at: project.updated_at
      }));

      setProjects(loadedProjects);
      
      // Apply initial filter if customer parameter exists
      const customerParam = searchParams.get('customer');
      if (customerParam) {
        applyFilter({
          ...currentFilter,
          customerName: customerParam
        });
      } else {
        setFilteredProjects(loadedProjects);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDataLoaded = async () => {
    await loadProjects();
  };

  const applyFilter = (filter: Filter) => {
    const filtered = projects.filter(project => {
      const matchesProject = !filter.projectName || 
        project.projectName
          .toLowerCase()
          .includes(filter.projectName.toLowerCase());

      const matchesCustomer = !filter.customerName || 
        (project.customer?.name || '')
          .toLowerCase() === filter.customerName.toLowerCase();

      const matchesTags = filter.selectedTags.length === 0 || 
        filter.selectedTags.every(tagName => 
          project.tags.some(tag => 
            tag.name.toLowerCase() === tagName.toLowerCase()
          )
        );

      const matchesStaff = filter.selectedStaff.length === 0 ||
        filter.selectedStaff.every(staffName =>
          project.staff.some(member =>
            member.name.toLowerCase() === staffName.toLowerCase()
          )
        );

      const matchesYear = !filter.year || project.year === filter.year;

      const matchesDuration = !filter.duration || 
        project.duration?.name === filter.duration;

      const matchesBudget = !filter.budget || 
        (project.budget || '').toLowerCase().includes(filter.budget.toLowerCase());

      const matchesFeatured = !filter.featured || project.featured;

      return (
        matchesProject && 
        matchesCustomer && 
        matchesTags && 
        matchesStaff && 
        matchesYear && 
        matchesDuration && 
        matchesBudget &&
        matchesFeatured
      );
    });

    setFilteredProjects(filtered);
  };

  const handleFilterChange = (filter: Filter) => {
    setCurrentFilter(filter);
    applyFilter(filter);
  };

  const handleNewProject = () => {
    const newProject: Project = {
      id: '',
      projectName: '',
      description: '',
      workload: 'moderate',
      featured: false,
      customer: { id: '', name: '' },
      images: [],
      tags: [],
      staff: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    setShowNewProject(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading projects...</div>
      </div>
    );
  }

  if (showSettings) {
    return <Settings onBack={() => setShowSettings(false)} />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="w-full px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Database className="w-8 h-8 text-indigo-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Project Dashboard</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleNewProject}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <SettingsIcon className="w-4 h-4 mr-2" />
              Settings
            </button>
          </div>
        </div>

        {projects.length === 0 ? (
          <FileUpload onDataLoaded={handleDataLoaded} />
        ) : (
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-3">
              <FilterPanel data={projects} onFilterChange={handleFilterChange} />
            </div>
            <div className="col-span-9">
              <div className="bg-white shadow rounded-lg">
                <ProjectTable projects={filteredProjects} onProjectUpdate={loadProjects} />
              </div>
            </div>
          </div>
        )}
      </div>

      {showNewProject && (
        <ProjectModal
          project={{
            id: '',
            projectName: '',
            description: '',
            workload: 'moderate',
            featured: false,
            customer: { id: '', name: '' },
            images: [],
            tags: [],
            staff: [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }}
          onClose={() => setShowNewProject(false)}
          onUpdate={() => {
            loadProjects();
            setShowNewProject(false);
          }}
          isNew={true}
        />
      )}
    </div>
  );
}